import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// KAMPANYA YETKİLERİ — LİSTE (menü 9120).
//
// Satır = YETKİ HEDEFİ (tek temsilci / temsilci grubu / temsilci tipi). Kural bazlı liste
// TERCİH EDİLMEDİ: kayıt hedef bazında full-replace edildiği için satır-bazlı Sil/Düzenle
// yanıltıcı olurdu (tek kuralı silmek ile "Yetki Yok" yapmak aynı şey değildir).
//
// Detay ekranı hedefin 13 detayını 5 sekmede tek yerden kaydeder → CampaignAuthorizationDetailPage.
const API = '/api/campaign-authorizations';
// c360 OData ucu — hedef bazlı projeksiyon (ODataResponse.List).
const API_BASE = `${API}/List`;

// Hedef ekseni. Sayısal değer aynı zamanda ÖZGÜLLÜK sırasıdır (büyük olan daha özel):
// belge kesilirken Temsilci(3) > Grup(2) > Tip(1) kazanır.
const TARGET = { TYPE: 1, GROUP: 2, REP: 3 };
const TARGET_LABEL = {
  [TARGET.REP]: 'Tek Temsilci',
  [TARGET.GROUP]: 'Temsilci Grubu',
  [TARGET.TYPE]: 'Temsilci Tipi',
};
const TARGET_OPTIONS = [
  { value: String(TARGET.REP), label: TARGET_LABEL[TARGET.REP] },
  { value: String(TARGET.GROUP), label: TARGET_LABEL[TARGET.GROUP] },
  { value: String(TARGET.TYPE), label: TARGET_LABEL[TARGET.TYPE] },
];

const COLUMN_FILTER_META = {
  targetType: { type: 'select', odataField: 'TargetType', valueFormat: 'raw', options: TARGET_OPTIONS },
  targetName: { type: 'text', odataField: 'TargetName' },
  targetCode: { type: 'text', odataField: 'TargetCode' },
  ruleCount: { type: 'number', odataField: 'RuleCount' },
};
const SEARCH_FIELDS = ['TargetName', 'TargetCode'];

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR');
}

export function CampaignAuthorizationsPage() {
  usePageMeta('Kampanya Yetkileri', 'Temsilci, grup ve tiplere tanımlı iskonto / fiyat / kampanya yetkileri.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'UpdatedDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const detailPath = (row) => `/campaign-authorizations/${row.targetType}/${row.targetId}`;
  const openNew = () => navigate('/campaign-authorizations/new');
  const openDetail = (row) => { if (row) navigate(detailPath(row), { state: { row } }); };
  // #282: kalem (Değiştir) düzenleme moduna açar; göz/çift-tık izleme modunda açar.
  const openEditRow = (row) => { if (row) navigate(`${detailPath(row)}?edit=1`, { state: { row } }); };

  /**
   * "Yetkileri Kaldır" — hedefin TÜM kurallarını siler (boş items ile full-replace).
   * Sonuç sıradan bir satır silme DEĞİLDİR: kural kalmayınca o hedefte denetim tamamen
   * kapanır ve kısıtsız (bugünkü) davranışa dönülür. Onay metni bunu açıkça söyler.
   */
  const handleClear = async () => {
    if (!selected) return;
    try {
      const res = await fetch(API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ targetType: selected.targetType, targetId: selected.targetId, items: [] }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Yetkiler kaldırılamadı');
      showToast('success', 'Yetkiler kaldırıldı — bu hedefte denetim kapandı');
      setClearConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
      setClearConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('targetType', {
      header: 'Kriter', meta: { filterType: 'select' },
      cell: (info) => TARGET_LABEL[info.getValue()] || '-',
    }),
    columnHelper.accessor('targetName', {
      header: 'Hedef', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('targetCode', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('ruleCount', {
      header: 'Kural', meta: { filterType: 'number' },
      // Katalogda 13 detay var; "7 / 13" yöneticiye kapsamı bir bakışta söyler.
      cell: (info) => `${info.getValue() ?? 0} / 13`,
    }),
    columnHelper.accessor('fullCount', {
      header: 'Tam Yetki', enableColumnFilter: false,
      cell: (info) => <StatusBadge tone={info.getValue() > 0 ? 'ok' : 'neutral'}>{info.getValue() ?? 0}</StatusBadge>,
    }),
    columnHelper.accessor('limitedCount', {
      header: 'Sınırlı', enableColumnFilter: false,
      // StatusBadge tonları: ok | wait | bad | neutral (StatusBadge.css) — "warn" yok.
      cell: (info) => <StatusBadge tone={info.getValue() > 0 ? 'wait' : 'neutral'}>{info.getValue() ?? 0}</StatusBadge>,
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Son Güncelleme', enableColumnFilter: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Yetkileri Kaldır" aria-label="Yetkileri Kaldır" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setClearConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    // Hedefin anahtarı BİLEŞİKTİR (tip + id) — tek bir `id` kolonu yok.
    getRowId: (row) => `${row.targetType}-${row.targetId}`,
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Kriter', valueGetter: ({ row }) => TARGET_LABEL[row.targetType] || '' },
        { field: 'targetName', headerName: 'Hedef' },
        { field: 'targetCode', headerName: 'Kod' },
        { field: 'ruleCount', headerName: 'Kural Sayısı' },
        { field: 'fullCount', headerName: 'Tam Yetki' },
        { field: 'limitedCount', headerName: 'Sınırlı' },
        { headerName: 'Son Güncelleme', valueGetter: ({ row }) => formatDateTime(row.updatedDate) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `kampanya_yetkileri_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Hedef adı veya kodu ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          aria-label="Excel'e Aktar"
          onClick={handleExport}
        >
          <ExcelIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kampanya Yetkileri"
        subtitle="Yetkisi tanımlı temsilci, grup ve tipler. Listede olmayan hedefte denetim uygulanmaz."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Yetki</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Henüz hiçbir hedefe yetki tanımlanmamış — denetim uygulanmıyor."
        />
      </ListPageShell>

      {clearConfirm && (
        <div style={s.overlay} onClick={() => setClearConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Yetkileri Kaldır</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>“{selected?.targetName}”</strong> hedefinin tüm yetki kuralları silinecek.
                <br /><br />
                Bu sıradan bir silme değildir: kural kalmayınca bu hedefte <strong>denetim tamamen kapanır</strong>
                {' '}ve iskonto/bedelsiz <strong>kısıtsız</strong> hâle döner. Devam edilsin mi?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleClear}>Evet, Kaldır</button>
              <button style={s.btnSecondary} onClick={() => setClearConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CampaignAuthorizationsPage;
